import type { ReactElement } from 'react';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { formatNumber } from '@xdocs/i18n';
import type { ReactionKind } from '@xdocs/schemas';
import { Button, cn, useI18n } from '@xdocs/ui';
import { optionalProp } from '../../lib/optionalProp.js';
import { useReaction } from './useReaction.js';
import { useSharingT } from './useSharingT.js';
import styles from './ReactionBar.module.css';

export interface ReactionBarProps {
  shareId: string;
  likeCount: number;
  dislikeCount: number;
  viewerReaction: ReactionKind | null;
  /** `false` for an anonymous viewer (F66 AC: link/public reads need no
   * account, but reacting still requires one) — both buttons stay visible
   * (never a dead/missing control) but disabled with a sign-in reason. */
  canReact: boolean;
  className?: string | undefined;
}

/** F29 like/dislike control — reused on discover cards and the public share
 * detail header. One `reactions` row per (shareId, viewerUserId); toggling
 * the active kind clears it, picking the other kind flips it. */
export function ReactionBar({ shareId, likeCount, dislikeCount, viewerReaction, canReact, className }: ReactionBarProps): ReactElement {
  const { locale } = useI18n();
  const { t } = useSharingT();
  const reaction = useReaction(shareId, { likeCount, dislikeCount, viewerReaction });
  const disabledReason = canReact ? undefined : t('sharing:reaction.signInToReact');

  return (
    <div className={cn(styles['bar'], className)} role="group" aria-label={t('sharing:reaction.like')}>
      <Button
        size="sm"
        variant="secondary"
        state={reaction.isPending ? 'loading' : 'idle'}
        disabled={!canReact}
        {...optionalProp('disabledReason', disabledReason)}
        aria-pressed={reaction.viewerReaction === 'like'}
        aria-label={reaction.viewerReaction === 'like' ? t('sharing:reaction.liked') : t('sharing:reaction.like')}
        className={cn(reaction.viewerReaction === 'like' && styles['active'])}
        onClick={() => reaction.toggle('like')}
      >
        <ThumbsUp size={15} aria-hidden="true" />
        {formatNumber(locale, reaction.likeCount, { notation: 'compact' })}
      </Button>
      <Button
        size="sm"
        variant="secondary"
        state={reaction.isPending ? 'loading' : 'idle'}
        disabled={!canReact}
        {...optionalProp('disabledReason', disabledReason)}
        aria-pressed={reaction.viewerReaction === 'dislike'}
        aria-label={reaction.viewerReaction === 'dislike' ? t('sharing:reaction.disliked') : t('sharing:reaction.dislike')}
        className={cn(reaction.viewerReaction === 'dislike' && styles['active'])}
        onClick={() => reaction.toggle('dislike')}
      >
        <ThumbsDown size={15} aria-hidden="true" />
        {formatNumber(locale, reaction.dislikeCount, { notation: 'compact' })}
      </Button>
    </div>
  );
}
